/**
 * Unique content builders for locality landing pages — reduces doorway-page sameness.
 */

const AREA_VENUE_NOTES = {
  "anna-nagar":
    "Tower banquet halls and wide residential streets make Anna Nagar ideal for groom-entry processions and evening reception Melam.",
  velachery:
    "South Chennai halls around Velachery and Alappakkam suit compact to mid-size ensembles that still deliver Pooram-level energy.",
  porur:
    "Western Chennai marriage grounds around Porur often allow larger Chenda lines for outdoor baraats and open-lawn receptions.",
  tambaram:
    "Tambaram and Chromepet venues blend city and suburban layouts — we plan entry routes and PA levels for each hall type.",
  omr:
    "OMR hotel ballrooms and IT-corridor resorts need stage-friendly Melam cues timed to walk-ins and photographer slots.",
  ecr:
    "ECR beach resorts and destination weddings call for travel-ready teams and weather-aware outdoor Melam placements.",
  adyar:
    "Adyar and Besant Nagar celebrations often prefer elegant compact ensembles for heritage homes and boutique venues.",
  tnagar:
    "T. Nagar marriage halls are high-energy and schedule-tight — we keep Melam blocks crisp for muhurtham and reception cues.",
  chromepet:
    "Chromepet and Pallavaram corridors host family halls where mid-size Melam packages are the most requested format.",
  avadi:
    "Avadi and Ambattur functions often combine temple visits with hall receptions — we coordinate Melam for both settings.",
  ambattur:
    "Ambattur industrial-suburb weddings favour powerful entry Melam that still fits indoor banquet acoustics.",
  medavakkam:
    "Medavakkam and south-east Chennai growth corridors need punctual teams for multi-hall weekend wedding schedules.",
  pallikaranai:
    "Pallikaranai venues near Velachery benefit from flexible team sizes for residential and community hall weddings.",
  perungudi:
    "Perungudi and OMR-side hotels book Melam for corporate-style receptions and Telugu–Kerala fusion weddings.",
  sholinganallur:
    "Sholinganallur destination hotels on OMR request premium Melam packages with clear cue sheets for planners.",
};

const CITY_VENUE_NOTES = {
  coimbatore:
    "Coimbatore’s Kongu wedding circuit loves grand entry Melam — we travel with full kits for halls across Peelamedu and Race Course.",
  madurai:
    "Madurai temple-town weddings pair well with traditional Melam textures for muhurtham and evening receptions.",
  hyderabad:
    "Hyderabad Telugu–Kerala fusion weddings are a specialty — baraat and reception Melam timed for large banquet floors.",
  vijayawada:
    "Vijayawada and coastal Andhra marriages often need travel-ready Melam teams for riverside and city hall venues.",
  tirupati:
    "Tirupati wedding calendars are dense — we confirm date and temple/hall logistics early for Melam bookings.",
  trichy:
    "Trichy celebrations across Srirangam and city halls suit classical Melam with clear muhurtham timing.",
  salem:
    "Salem and western Tamil Nadu halls book Melam for both traditional and fusion wedding formats.",
  visakhapatnam:
    "Vizag coastal venues need outdoor-friendly Melam setups with backup indoor cues for weather changes.",
};

const BOOKING_STEPS = (name) => [
  `Share your wedding date and venue name in ${name} on WhatsApp.`,
  "Tell us the moments you need Melam for — entry, baraat, muhurtham, or reception.",
  "We confirm team size, duration, travel, and a transparent package quote.",
  "Lock the booking with schedule cues for your photographer and wedding planner.",
];

export const getLocalityVenueNote = (locality) => {
  if (locality.kind === "area") {
    return (
      AREA_VENUE_NOTES[locality.slug] ||
      `${locality.name} venues vary from compact halls to open lawns — we match ensemble size to your space and guest count.`
    );
  }
  return (
    CITY_VENUE_NOTES[locality.slug] ||
    `We travel to ${locality.name}, ${locality.state} with a professional Kerala Chenda Melam team sized for your venue and guest list.`
  );
};

export const getLocalityBookingSteps = (locality) => BOOKING_STEPS(locality.name);

export const getLocalityWhyUs = (locality) => {
  const place = locality.kind === "area" ? `${locality.name}, Chennai` : `${locality.name}, ${locality.state}`;
  return [
    {
      title: `On-time Melam in ${locality.name}`,
      text: `Punctual arrival and cue-based entries for ${place} wedding schedules.`,
    },
    {
      title: "Authentic Kerala repertoire",
      text: "Panchari-inspired wedding sets, entry Melam, and optional Panchavadyam colours.",
    },
    {
      title: "Packages that fit the venue",
      text: "From compact entry teams to fuller ensembles — priced for duration and travel.",
    },
  ];
};

export const getBreadcrumbSchema = (locality, path) => {
  const items = [
    { name: "Home", path: "/" },
    { name: "Wedding Melam", path: "/chenda-melam-chennai" },
    {
      name: `Chenda Melam ${locality.name}`,
      path,
    },
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `https://thiruvambadyvadhyasangam.com${item.path === "/" ? "" : item.path}`,
    })),
  };
};

export const getServiceSchema = (locality, path) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: `Chenda Melam for Wedding in ${locality.name}`,
  serviceType: "Kerala Chenda Melam performance",
  provider: {
    "@type": "MusicGroup",
    name: "Thiruvambady Vadhya Sangam & Kalakshethra",
  },
  areaServed: {
    "@type": locality.kind === "area" ? "Place" : "City",
    name: locality.kind === "area" ? `${locality.name}, Chennai` : locality.name,
  },
  url: `https://thiruvambadyvadhyasangam.com${path}`,
  description: `Book Kerala Chenda Melam for wedding in ${locality.name} — groom entry, baraat, reception, and muhurtham.`,
});
